import React from "react";
import type { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Switch,
} from "@/components";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { RequiredLabel } from "@/components/ui/required-label";
import { useI18n } from "@/context/i18n";

import type { TProductFormData } from "../../products.schema";

export interface ProductMaximumPriceFieldsProps {
  form: UseFormReturn<TProductFormData>;
  cfgPrefix: string;
}

type MaxPriceField = {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
};

function ProductMaximumPriceField({ field }: { field: MaxPriceField }) {
  const { t } = useI18n();
  const [isUnlimited, setIsUnlimited] = React.useState(
    field.value === null || field.value === 0,
  );
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (field.value === 0) {
        field.onChange(null);
        setIsUnlimited(true);
      } else if (field.value === null) {
        setIsUnlimited(true);
      } else {
        setIsUnlimited(false);
      }
    }
  }, [field]);

  return (
    <FormItem>
      <div className="flex items-center justify-between mb-2">
        <RequiredLabel required={!isUnlimited}>
          {t("products.pricing.maximum_price_product")}
        </RequiredLabel>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("products.gateway.plan.unlimited_max")}
          </span>
          <Switch
            checked={isUnlimited}
            onCheckedChange={(checked) => {
              setIsUnlimited(checked);
              if (checked) {
                field.onChange(null);
              } else {
                field.onChange(0);
              }
            }}
          />
        </div>
      </div>
      <FormControl>
        <RealCurrencyInput
          value={Number(field.value ?? 0)}
          onChange={field.onChange}
          disabled={isUnlimited}
        />
      </FormControl>
      {isUnlimited ? (
        <p className="text-xs text-green-600 mt-1">
          {t("products.gateway.plan.unlimited_max_active")}
        </p>
      ) : null}
      <FormMessage />
    </FormItem>
  );
}

export const ProductMaximumPriceFields: React.FC<ProductMaximumPriceFieldsProps> = ({
  form,
  cfgPrefix,
}) => (
  <FormField
    control={form.control}
    name={`${cfgPrefix}.maximum_price` as "warranty_product_config.maximum_price"}
    render={({ field }) => <ProductMaximumPriceField field={field} />}
  />
);

/** @deprecated use ProductMaximumPriceFields */
export const ProductMinMaxPriceFields = ProductMaximumPriceFields;
